/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type {
  GitHubPullRequestReviewThread,
  GitHubPullRequestReviewThreadComment,
} from './github/pullRequestTimelineTypes';

import ActorHeading from './ActorHeading';
import {CommentActionsMenu, EditCommentForm, useDeleteComment} from './CommentActions';
import PendingLabel from './PendingLabel';
import PullRequestInlineCommentInput from './PullRequestInlineCommentInput';
import ResolveThreadButton from './ResolveThreadButton';
import TrustedRenderedMarkdown from './TrustedRenderedMarkdown';
import timeAgo from './timeAgo';
import {PullRequestReviewCommentState} from './generated/graphql';
import {gitHubPullRequestHoveredThreadRangeAtom, gitHubPullRequestJumpToCommentIDAtom} from './jotai/atoms';
import {cancelScrollCorrection} from './scrollToFileElement';
import {CheckCircleIcon} from '@primer/octicons-react';
import {Box, Button, Flash, StyledOcticon, Text} from '@primer/react';
import {useAtom, useSetAtom} from 'jotai';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

type Props = {
  thread: GitHubPullRequestReviewThread;
};

export default function InlineCommentThread({thread}: Props): React.ReactElement | null {
  const {comments} = thread;
  const [showReply, setShowReply] = useState(false);
  const [collapsed, setCollapsed] = useState(thread.isResolved);
  const setHoveredRange = useSetAtom(gitHubPullRequestHoveredThreadRangeAtom);

  const threadRange = useMemo(() => {
    const endLine = thread.originalLine;
    if (endLine == null) return null;
    const firstComment = thread.comments[0];
    if (firstComment == null) return null;
    const startLine = thread.originalStartLine ?? endLine;
    return {
      path: firstComment.path,
      startLine,
      endLine,
      side: thread.startDiffSide ?? thread.diffSide,
    };
  }, [thread]);

  const onMouseEnter = useCallback(() => {
    if (threadRange != null) setHoveredRange(threadRange);
  }, [threadRange, setHoveredRange]);

  const onMouseLeave = useCallback(() => {
    setHoveredRange(null);
  }, [setHoveredRange]);
  const lastComment = comments[comments.length - 1];
  const firstComment = comments[0];

  // Watch for jump-to-comment signals on all comments in this thread.
  // If the thread is collapsed when a jump is triggered, expand it so the
  // Comment component can handle the scroll-into-view.
  const firstCommentJumpAtom = gitHubPullRequestJumpToCommentIDAtom(firstComment?.id ?? '');
  const lastCommentJumpAtom = gitHubPullRequestJumpToCommentIDAtom(lastComment?.id ?? '');
  const [firstJump, setFirstJump] = useAtom(firstCommentJumpAtom);
  const [lastJump, setLastJump] = useAtom(lastCommentJumpAtom);

  useEffect(() => {
    if (collapsed && (firstJump || lastJump)) {
      setCollapsed(false);
      // Re-set the jump flags so the Comment component picks them up after expanding
      if (firstJump) {
        setFirstJump(true);
      }
      if (lastJump) {
        setLastJump(true);
      }
    }
  }, [collapsed, firstJump, lastJump, setFirstJump, setLastJump]);

  if (lastComment == null) {
    return null;
  }

  const commentID = lastComment.id;
  const commitID = lastComment.originalCommit?.oid;
  const canReply = commitID != null;

  if (collapsed) {
    return (
      <Box backgroundColor="canvas.subtle" fontFamily="normal" padding={2}>
        <Box
          display="flex"
          alignItems="center"
          backgroundColor="canvas.default"
          borderColor="border.default"
          borderWidth={1}
          borderStyle="solid"
          paddingX={2}
          paddingY={1}
          sx={{gap: 1, cursor: 'pointer'}}
          onClick={() => setCollapsed(false)}>
          <StyledOcticon icon={CheckCircleIcon} size={14} color="done.fg" />
          <Text fontSize={12} color="fg.muted">
            Resolved conversation — click to expand
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box backgroundColor="canvas.subtle" fontFamily="normal" padding={2} sx={{position: 'relative', zIndex: 1}} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <Box
        backgroundColor="canvas.default"
        borderColor="border.default"
        borderWidth={1}
        borderStyle="solid">
        {comments.map((comment, index) => (
          <Comment key={index} comment={comment} />
        ))}
        {showReply && canReply ? (
          <PullRequestInlineCommentInput
            commentID={commentID}
            commitID={commitID}
            onCancel={() => setShowReply(false)}
          />
        ) : (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            backgroundColor="canvas.subtle"
            borderTopColor="border.default"
            borderTopWidth={1}
            borderTopStyle="solid"
            padding={2}>
            <ResolveThreadButton threadId={thread.id} isResolved={thread.isResolved} />
            <Box display="flex" alignItems="center" sx={{gap: 2}}>
              {thread.isResolved && (
                <Text
                  as="button"
                  fontSize={12}
                  onClick={() => setCollapsed(true)}
                  sx={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    color: 'fg.muted',
                    '&:hover': {textDecoration: 'underline'},
                  }}>
                  Collapse
                </Text>
              )}
              {canReply && (
                <Button onClick={() => setShowReply(true)}>Reply</Button>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function Comment({comment}: {comment: GitHubPullRequestReviewThreadComment}): React.ReactElement {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [jumpToCommentID, setJumpToCommentID] = useAtom(
    gitHubPullRequestJumpToCommentIDAtom(comment.id),
  );
  const {deleteComment, isDeleting, deleteError} = useDeleteComment(comment.id);

  useEffect(() => {
    if (ref.current != null && jumpToCommentID) {
      // Cancel any file-level scroll correction loop so it doesn't fight
      // this comment-level scroll.
      cancelScrollCorrection();
      ref.current.scrollIntoView({block: 'center'});
      setJumpToCommentID(false);
    }
  }, [jumpToCommentID, setJumpToCommentID]);

  const onEdit = useCallback(() => setIsEditing(true), []);
  const onCancelEdit = useCallback(() => setIsEditing(false), []);

  let pendingLabel = null;
  if (comment.state === PullRequestReviewCommentState.Pending) {
    pendingLabel = <PendingLabel />;
  }

  return (
    <Box ref={ref} padding={2} sx={isDeleting ? {opacity: 0.5, pointerEvents: 'none'} : undefined}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <ActorHeading
          actor={comment.author}
          timestamp={comment.createdAt != null ? timeAgo(comment.createdAt) : undefined}
        />
        <Box display="flex" alignItems="center" sx={{gap: 1}}>
          {pendingLabel}
          <CommentActionsMenu
            viewerCanUpdate={comment.viewerCanUpdate}
            viewerCanDelete={comment.viewerCanDelete}
            onEdit={onEdit}
            onDelete={deleteComment}
          />
        </Box>
      </Box>
      {deleteError != null && (
        <Flash variant="danger" sx={{mt: 1}}>
          {deleteError}
        </Flash>
      )}
      {isEditing ? (
        <EditCommentForm
          commentId={comment.id}
          initialBody={comment.body}
          onCancel={onCancelEdit}
          onSaved={onCancelEdit}
        />
      ) : (
        <Box fontSize={1} sx={{wordBreak: 'break-word'}}>
          <TrustedRenderedMarkdown trustedHTML={comment.bodyHTML} />
        </Box>
      )}
    </Box>
  );
}


